import * as i0 from '@angular/core';
import { EventEmitter, Component, Input, Output, ViewChild, NgModule } from '@angular/core';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i3 from '@angular/forms';
import { NgControl, FormsModule } from '@angular/forms';
import * as i1 from '@capturum/ui/api';
import { ValueAccessorBase, CapturumSharedModule } from '@capturum/ui/api';
import * as i4 from 'primeng/inputnumber';
import { InputNumber, InputNumberModule } from 'primeng/inputnumber';
function CapturumInputNumberComponent_label_1_span_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(ctx_r2.label);
  }
}
const _c0 = function (a0) {
  return {
    "justify-content": a0
  };
};
function CapturumInputNumberComponent_label_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "label");
    i0.ɵɵtemplate(1, CapturumInputNumberComponent_label_1_span_1_Template, 2, 1, "span", 5);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵstyleMap(i0.ɵɵpureFunction1(3, _c0, ctx_r0.label ? "space-between" : "flex-end"));
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r0.label);
  }
}
function CapturumInputNumberComponent_cap_validator_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "cap-validator", 6);
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("control", ctx_r1.control)("customName", ctx_r1.label);
  }
}
var InputNumberButtonLayout = /*#__PURE__*/function (InputNumberButtonLayout) {
  InputNumberButtonLayout["stacked"] = "stacked";
  InputNumberButtonLayout["horizontal"] = "horizontal";
  InputNumberButtonLayout["vertical"] = "vertical";
  return InputNumberButtonLayout;
}(InputNumberButtonLayout || {});
var InputNumberLocaleMatcher = /*#__PURE__*/function (InputNumberLocaleMatcher) {
  InputNumberLocaleMatcher["lookup"] = "lookup";
  InputNumberLocaleMatcher["bestFit"] = "best fit";
  return InputNumberLocaleMatcher;
}(InputNumberLocaleMatcher || {});
var InputNumberMode = /*#__PURE__*/function (InputNumberMode) {
  InputNumberMode["decimal"] = "decimal";
  InputNumberMode["currency"] = "currency";
  return InputNumberMode;
}(InputNumberMode || {});
var InputNumberCurrencyDisplay = /*#__PURE__*/function (InputNumberCurrencyDisplay) {
  InputNumberCurrencyDisplay["symbol"] = "symbol";
  InputNumberCurrencyDisplay["name"] = "name";
  return InputNumberCurrencyDisplay;
}(InputNumberCurrencyDisplay || {});
let CapturumInputNumberComponent = /*#__PURE__*/(() => {
  class CapturumInputNumberComponent extends ValueAccessorBase {
    constructor(injector, validatorService) {
      super();
      this.injector = injector;
      this.validatorService = validatorService;
      /* Whether to format the value. */
      this.format = true;
      /* Displays spinner buttons. */
      this.showButtons = false;
      /** Layout of the buttons, valid values are "stacked" (default), "horizontal" and "vertical". */
      this.buttonLayout = InputNumberButtonLayout.stacked;
      /** Style class of the increment button. */
      this.incrementButtonClass = 'success';
      /** Style class of the decrement button. */
      this.decrementButtonClass = 'error';
      /** Icon of the increment button. */
      this.incrementButtonIcon = 'fas fa-plus';
      /** Icon of the decrement button. */
      this.decrementButtonIcon = 'fas fa-minus';
      /**
       * The locale matching algorithm to use. Possible values are "lookup" and "best fit";
       * the default is "best fit". See Locale Negotation for details.
       */
      this.localeMatcher = InputNumberLocaleMatcher.bestFit;
      /** Defines the behavior of the component, valid values are "decimal" and "currency". */
      this.mode = InputNumberMode.decimal;
      /**
       * How to display the currency in currency formatting.
       * Possible values are "symbol" to use a localized currency symbol such as €
       * "code" to use the ISO currency code
       * "name" to use a localized currency name such as "dollar";
       * the default is "symbol".
       */
      this.currencyDisplay = InputNumberCurrencyDisplay.symbol;
      /** Whether to use grouping separators, such as thousands separators or thousand/lakh/crore separators. */
      this.useGrouping = true;
      /** Step factor to increment/decrement the value. */
      this.step = 1;
      /**
       * When present, it specifies that the input cannot be typed
       */
      this.readonly = false;
      /* Callback on input blur */
      this.blur = new EventEmitter();
      /* Callback on input focus */
      this.focus = new EventEmitter();
    }
    ngAfterViewInit() {
      setTimeout(() => {
        const ngControl = this.injector.get(NgControl, null);
        this.control = this.validatorService.extractFormControl(ngControl);
      });
      // If input number has a prefix, On click event PrimeNG set then the cursor after the prefix
      // this prevents select the input on focus.
      // That's why we need to override this onInputClick method.
      const originalClickFn = this.inputNumber.onInputClick.bind(this.inputNumber);
      if (!this.inputNumber.readonly && this.prefix && this.selectOnFocus) {
        this.inputNumber.onInputClick = () => null;
      } else {
        this.inputNumber.onInputClick = originalClickFn;
      }
    }
    setDisabledState(isDisabled) {
      this.disabled = isDisabled;
    }
    onBlur(event) {
      this.blur.emit(event);
      this.touch();
    }
    onFocus(event) {
      this.focus.emit(event);
      if (this.selectOnFocus) {
        const input = this.inputNumber?.el?.nativeElement?.querySelector('input');
        if (input) {
          input.select();
        }
      }
    }
    static {
      this.ɵfac = function CapturumInputNumberComponent_Factory(t) {
        return new (t || CapturumInputNumberComponent)(i0.ɵɵdirectiveInject(i0.Injector), i0.ɵɵdirectiveInject(i1.ValidatorService));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: CapturumInputNumberComponent,
        selectors: [["cap-input-number"]],
        viewQuery: function CapturumInputNumberComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(InputNumber, 5);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.inputNumber = _t.first);
          }
        },
        inputs: {
          placeholder: "placeholder",
          format: "format",
          showButtons: "showButtons",
          buttonLayout: "buttonLayout",
          incrementButtonClass: "incrementButtonClass",
          decrementButtonClass: "decrementButtonClass",
          incrementButtonIcon: "incrementButtonIcon",
          decrementButtonIcon: "decrementButtonIcon",
          locale: "locale",
          localeMatcher: "localeMatcher",
          mode: "mode",
          prefix: "prefix",
          suffix: "suffix",
          currency: "currency",
          currencyDisplay: "currencyDisplay",
          useGrouping: "useGrouping",
          minFractionDigits: "minFractionDigits",
          maxFractionDigits: "maxFractionDigits",
          min: "min",
          max: "max",
          step: "step",
          styleClass: "styleClass",
          maxlength: "maxlength",
          tabindex: "tabindex",
          disabled: "disabled",
          autocomplete: "autocomplete",
          label: "label",
          selectOnFocus: "selectOnFocus",
          readonly: "readonly"
        },
        outputs: {
          blur: "blur",
          focus: "focus"
        },
        features: [i0.ɵɵProvidersFeature([ValueAccessorBase.getProviderConfig(CapturumInputNumberComponent)]), i0.ɵɵInheritDefinitionFeature],
        decls: 5,
        vars: 30,
        consts: [[1, "form-element"], [3, "style", 4, "ngIf"], [1, "p-inputgroup"], [3, "ngModel", "format", "showButtons", "buttonLayout", "locale", "localeMatcher", "mode", "prefix", "suffix", "currency", "currencyDisplay", "useGrouping", "minFractionDigits", "maxFractionDigits", "min", "max", "step", "styleClass", "placeholder", "maxlength", "tabindex", "disabled", "autocomplete", "decrementButtonClass", "incrementButtonClass", "decrementButtonIcon", "incrementButtonIcon", "readonly", "ngModelChange", "onFocus", "onBlur"], [3, "control", "customName", 4, "ngIf"], [4, "ngIf"], [3, "control", "customName"]],
        template: function CapturumInputNumberComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 0);
            i0.ɵɵtemplate(1, CapturumInputNumberComponent_label_1_Template, 2, 5, "label", 1);
            i0.ɵɵelementStart(2, "div", 2)(3, "p-inputNumber", 3);
            i0.ɵɵlistener("ngModelChange", function CapturumInputNumberComponent_Template_p_inputNumber_ngModelChange_3_listener($event) {
              return ctx.value = $event;
            })("onFocus", function CapturumInputNumberComponent_Template_p_inputNumber_onFocus_3_listener($event) {
              return ctx.onFocus($event);
            })("onBlur", function CapturumInputNumberComponent_Template_p_inputNumber_onBlur_3_listener($event) {
              return ctx.onBlur($event);
            });
            i0.ɵɵelementEnd()();
            i0.ɵɵtemplate(4, CapturumInputNumberComponent_cap_validator_4_Template, 1, 2, "cap-validator", 4);
            i0.ɵɵelementEnd();
          }
          if (rf & 2) {
            i0.ɵɵadvance(1);
            i0.ɵɵproperty("ngIf", ctx.label);
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("ngModel", ctx.value)("format", ctx.format)("showButtons", ctx.showButtons)("buttonLayout", ctx.buttonLayout)("locale", ctx.locale)("localeMatcher", ctx.localeMatcher)("mode", ctx.mode)("prefix", ctx.prefix)("suffix", ctx.suffix)("currency", ctx.currency)("currencyDisplay", ctx.currencyDisplay)("useGrouping", ctx.useGrouping)("minFractionDigits", ctx.minFractionDigits)("maxFractionDigits", ctx.maxFractionDigits)("min", ctx.min)("max", ctx.max)("step", ctx.step)("styleClass", ctx.styleClass)("placeholder", ctx.placeholder)("maxlength", ctx.maxlength)("tabindex", ctx.tabindex)("disabled", ctx.disabled)("autocomplete", ctx.autocomplete)("decrementButtonClass", "cap-button " + ctx.decrementButtonClass)("incrementButtonClass", "cap-button " + ctx.incrementButtonClass)("decrementButtonIcon", ctx.decrementButtonIcon)("incrementButtonIcon", ctx.incrementButtonIcon)("readonly", ctx.readonly);
            i0.ɵɵadvance(1);
            i0.ɵɵproperty("ngIf", ctx.control);
          }
        },
        dependencies: [i2.NgIf, i3.NgControlStatus, i3.MaxLengthValidator, i3.NgModel, i4.InputNumber, i1.CapturumValidatorComponent],
        styles: [".ng-invalid[_nghost-%COMP%]   input.ng-touched[_ngcontent-%COMP%]{border:1px solid var(--error-color)}"]
      });
    }
  }
  return CapturumInputNumberComponent;
})();
(function () {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let CapturumInputNumberModule = /*#__PURE__*/(() => {
  class CapturumInputNumberModule {
    static {
      this.ɵfac = function CapturumInputNumberModule_Factory(t) {
        return new (t || CapturumInputNumberModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: CapturumInputNumberModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [CommonModule, FormsModule, InputNumberModule, CapturumSharedModule]
      });
    }
  }
  return CapturumInputNumberModule;
})();
(function () {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { CapturumInputNumberComponent, CapturumInputNumberModule, InputNumberButtonLayout, InputNumberCurrencyDisplay, InputNumberLocaleMatcher, InputNumberMode };
